import React,{useState,useEffect,useLayoutEffect,useRef} from 'react';
import {InputAdornment,Tooltip,Stack,Button,ButtonGroup,TextField,Box,Select,MenuItem,Checkbox} from '@mui/material';
import {styled} from '@mui/material/styles';
import {DataGrid,Toolbar,ToolbarButton,QuickFilter,QuickFilterControl,QuickFilterClear,QuickFilterTrigger} from '@mui/x-data-grid';
import SearchIcon from '@mui/icons-material/Search';
import CancelIcon from '@mui/icons-material/Cancel';
import {fiFI,ptBR} from '@mui/x-data-grid/locales';
import {consoleLog,saveUserDataGridParams,listGridUpdated} from '../../utilities/Utilities';
import {columns} from '../../../custom/entities/Lists.js';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import RefreshIcon from '@mui/icons-material/Refresh';

// Fixed-position Footer.js is 20px tall and isn't part of document flow, so it
// must be subtracted manually when measuring remaining viewport space.
const FOOTER_HEIGHT=20;
const GRID_BOTTOM_MARGIN=10;
const MIN_GRID_HEIGHT=300;

const StyledQuickFilter=styled(QuickFilter)({
  display:'grid',
  alignItems:'center',
});

const StyledToolbarButton = styled(ToolbarButton)(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  width: 'min-content',
  height: 'min-content',
  zIndex: 1,
  opacity: ownerState.expanded ? 0 : 1,
  pointerEvents: ownerState.expanded ? 'none' : 'auto',
  transition: theme.transitions.create(['opacity']),
}));

const StyledTextField = styled(TextField)(({ theme, ownerState }) => ({
  gridArea: '1 / 1',
  overflowX: 'clip',
  width: ownerState.expanded ? '100%' : 'var(--trigger-width)',
  opacity: ownerState.expanded ? 1 : 0,
  transition: theme.transitions.create(['width', 'opacity']),
}));

export const SearchToolbar = React.memo(function SearchToolbar({
  filterModel,
  setFilterModel,
  searchLabel,
  searchTooltip,
  focusOnSearchInput,
  application,
  list_name,
  isPopUp,
  columnsForFilter,
  quickSearchColumns,
  setQuickSearchColumns,
}) {
  const firstFocus = useRef(true);
  const [localSearch, setLocalSearch] = useState(
    filterModel?.quickFilterValues?.[0] ?? ''
  );
  const [expanded, setExpanded] = useState(false);
  const [userOpenedSearch, setUserOpenedSearch] = useState(false);

  useEffect(() => {
    if (focusOnSearchInput && firstFocus.current) {
      setExpanded(true);
      firstFocus.current = false;
    }
  }, [focusOnSearchInput]);

  const handleColumnChange = (event) => {
    const value = event.target.value;
    const next = value.length === 0 || value.length === columnsForFilter.length ? null : value;
    setQuickSearchColumns(next);
  };

  const handleSearchChange = (event) => {
  const value = event.target.value;
  setLocalSearch(value);
  const next = {
    ...filterModel,
    quickFilterValues: value ? value.trim().split(/\s+/) : [],
  };
  setFilterModel(next);
  
  if(!isPopUp){
    application.updateGlobalData({
      ...application.globalData,
      filterModel: {
        ...application.globalData.filterModel,
        [list_name]: next,
      },
    });
  }
};

  const isExpanded = expanded || userOpenedSearch;
  const hasColumnSelect = columnsForFilter?.length > 0 && isExpanded;

  // MUI DataGrid measures the toolbar's height once and doesn't watch for it
  // changing shape in place (e.g. this quick filter expanding to a second row),
  // which otherwise leaves the column headers/rows drawn over the new row.
  // Dispatching a resize nudges the grid to re-measure and lay out correctly.
  useEffect(() => {
    window.dispatchEvent(new Event('resize'));
  }, [isExpanded]);

  return (
    <>
      {hasColumnSelect && (
        <Select
          multiple
          size="small"
          displayEmpty
          value={quickSearchColumns ?? []}
          onChange={handleColumnChange}
          renderValue={(selected) =>
            selected.length === 0
              ? <em style={{ fontSize: '0.875rem', color: '#aaa' }}>{application.getLocale("LabelAll")}</em>
              : selected.map(f => columnsForFilter.find(c => c.field === f)?.headerName ?? f).join(', ')
          }
          sx={{
            fontSize: '0.875rem',
            minWidth: 80,
            maxWidth: 160,
            flexShrink: 0,
            marginLeft: 'auto',
            '& .MuiSelect-select': { paddingTop: '5.5px', paddingBottom: '5.5px' },
          }}
          MenuProps={{ PaperProps: { sx: { maxHeight: 300 } } }}
        >
          {columnsForFilter.map(col => (
            <MenuItem key={col.field} value={col.field} dense>
              <Checkbox size="small" checked={(quickSearchColumns ?? []).includes(col.field)} sx={{ py: 0, pr: 0.5, pl: 0 }} />
              {col.headerName}
            </MenuItem>
          ))}
        </Select>
      )}

      <StyledQuickFilter
        expanded={isExpanded}
        onExpandedChange={(newExpanded) => {
          if (newExpanded) {
            setExpanded(true);
          } else {
            setExpanded(false);
            setUserOpenedSearch(false);
          }
        }}
        sx={{
          flexShrink: 0,
          flexGrow: 1,
          alignItems: 'center',
          marginLeft: hasColumnSelect ? 0 : 'auto',
        }}
      >
        <QuickFilterTrigger
          render={(triggerProps, state) => (
            <Tooltip title={searchLabel} enterDelay={0}>
              <StyledToolbarButton
                {...triggerProps}
                onClick={(e) => {
                  triggerProps.onClick?.(e);
                  setExpanded(true);
                  setUserOpenedSearch(true);
                  firstFocus.current = false;
                }}
                ownerState={{ expanded: state.expanded }}
                color="default"
              >
                <SearchIcon fontSize="small" />
              </StyledToolbarButton>
            </Tooltip>
          )}
        />

        <QuickFilterControl
          value={localSearch ? [localSearch] : []}
          onChange={handleSearchChange}
          render={({ ref, ...controlProps }, state) => (
            <StyledTextField
              {...controlProps}
              ownerState={{ expanded: state.expanded }}
              autoFocus={state.expanded}
              value={localSearch}
              onChange={handleSearchChange}
              inputRef={ref}
              placeholder={searchTooltip}
              size="small"
              slotProps={{
                input: {
                  sx: {
                    '& .MuiInputBase-input': {
                      padding: '5.5px 14px',
                    },
                    fontSize: '0.875rem',
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                  endAdornment: state.value ? (
                    <InputAdornment position="end">
                      <QuickFilterClear
                        edge="end"
                        size="small"
                        onClick={() => handleSearchChange({ target: { value: '' } })}
                        material={{ sx: { marginRight: -0.75 } }}
                      >
                        <CancelIcon fontSize="small" />
                      </QuickFilterClear>
                    </InputAdornment>
                  ) : null,
                },
              }}
            />
          )}
        />
      </StyledQuickFilter>
    </>
  );
});

export const ButtonsToolbar = React.memo(function ButtonsToolbar({
  application,
  button_actions,
  canRemoveSelection,
  canCreateNew,
  entityRules,
  noRefresh,
  filterComponent,
  handleClickEvent,
}) {
  return Object.keys(entityRules||{}).length>0?(
    <Box sx={{ flex: "8 0 auto", minWidth: "fit-content" }}>
    <Stack direction={{xs:'column', sm: 'row' }} spacing={{ xs: 1, sm: 2, md: 2 }} justifyContent="space-between" alignItems="center" sx={{ width: "100%" }}>
      <ButtonGroup size="small" sx={{ alignSelf: "flex-start" }}>
        {entityRules?.can_create && canCreateNew && (
          <Tooltip
            title={application.getLocale("ButtonNewRecord")}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              sx={{display:'flex','& .MuiButton-startIcon':{marginRight:0,},ml:'0px',alignItems:'center',justifyContent:'center',}}
              onClick={() => handleClickEvent({ action: 'new' })}
            />
          </Tooltip>
        )}

        {canRemoveSelection && (
          <Tooltip
            title={application.getLocale("ButtonDelete")}>
            <Button
              color="error"
              variant="outlined"
              startIcon={<RemoveIcon />}
              sx={{display:'flex','& .MuiButton-startIcon':{marginRight:0,},ml:'0px',alignItems:'center',justifyContent:'center',}}
              onClick={() => handleClickEvent({ action: 'removeSelection' })}
            />
          </Tooltip>
        )}

        {!noRefresh && (
          <Tooltip
            title={application.getLocale("ButtonRefresh")}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              sx={{display:'flex','& .MuiButton-startIcon':{marginRight:0,},ml:'0px',alignItems:'center',justifyContent:'center',}}
              onClick={() => handleClickEvent({ action: 'refresh' })}
            />
          </Tooltip>
        )}

        {button_actions&&button_actions.map((action,index)=>(
            <Button
              key={index}
              variant={action.variant||'outlined'}
              color={action.color||'primary'}
              name={action.name}
              onClick={()=>handleClickEvent({action:action.name,args:action.args})}>
              {application.getLocale(action?.label)}
            </Button>
          ))}
      </ButtonGroup>
      {filterComponent}
    </Stack>
    </Box>
  ): null;
});

function CombinedToolbar(props) {
  const { searchProps, buttonProps } = props;

  return (
    <Toolbar sx={{ border: 'none', display: 'flex', flexWrap: 'wrap', rowGap: 0, minHeight: 'auto', width: '100%' }}>
      <ButtonsToolbar {...buttonProps} />
      <SearchToolbar {...searchProps} />
    </Toolbar>
  );
}

const List=({application,records,list_name,loading,height,file,canCreateNew=true,initialState,userData,button_actions,handleClickEvent,handleSelection,checkboxSelection,disableRowSelectionOnClick,disableSelectionOnClick,selectionRequiresDeletePermission=true,rowSelectionModel,filterComponent,canRemoveSelection,entityRules,getRowClassName=null,dataGridSx=null,noRefresh=false,focusOnSearchInput=false,isPopUp=false,defaultQuickSearchColumns})=>{
    const [filterModel,setFilterModel]=useState(isPopUp?undefined:application?.globalData?.filterModel?.[list_name]);
    const contentRef=useRef(null);
    const [autoHeight,setAutoHeight]=useState(window.innerHeight-170);

    // When no explicit height is given, fill the gap between the grid and the
    // fixed footer instead of guessing it with a hardcoded offset. A ResizeObserver
    // on the body (rather than just a window resize listener) catches layout shifts
    // caused by content above the grid changing size in place - e.g. a PageDetail
    // form's show-more/show-less toggle pushing a page-tab List up or down without
    // the viewport itself resizing. MIN_GRID_HEIGHT keeps the grid usable even when
    // that content above it is tall enough to leave little room.
    useLayoutEffect(()=>{
        if(height!==undefined) return;
        const computeAutoHeight=()=>{
            if(!contentRef.current) return;
            const top=contentRef.current.getBoundingClientRect().top;
            const available=window.innerHeight-top-FOOTER_HEIGHT-GRID_BOTTOM_MARGIN;
            setAutoHeight(Math.max(available,MIN_GRID_HEIGHT));
        };
        computeAutoHeight();
        window.addEventListener('resize',computeAutoHeight);
        const resizeObserver=new ResizeObserver(computeAutoHeight);
        resizeObserver.observe(document.body);
        return()=>{
            window.removeEventListener('resize',computeAutoHeight);
            resizeObserver.disconnect();
        };
    },[height]);

    const gridHeight=height!==undefined?height:autoHeight;
    const [sortModel,setSortModel]=useState(application?.globalData?.sortModel?.[list_name]);
    const [shouldFocusSearch,setShouldFocusSearch]=useState(focusOnSearchInput);
    const [quickSearchColumns,setQuickSearchColumns]=useState(defaultQuickSearchColumns??null);
    const [localColumnWidths,setLocalColumnWidths]=useState(isPopUp?(application?.globalData?.dataGridParams?.[list_name]??{}):null);

    const getLocale=(lang)=>{
        switch(lang){
            case "fi":
                return fiFI.components.MuiDataGrid.defaultProps.localeText;
            case "pt-BR":
                return ptBR.components.MuiDataGrid.defaultProps.localeText;
            default:
                return null;
        }
    }

    const getChangedFields=(oldRow,newRow)=>{
      const changed={};
      for(const key in newRow){
        if(newRow[key]!==oldRow[key]){
            changed[key]=newRow[key];
        }
      }
      return {entityId:oldRow.id,changed};
    };

    const processRowUpdate=async(newRow,oldRow)=>{
      const {entityId,changed}=getChangedFields(oldRow,newRow);
      if(Object.keys(changed).length===0){return oldRow;}
      const entityData={}
      Object.entries(changed).forEach(([field,value])=>{
          entityData[field]=value
      });
      const data=await handleClickEvent({action:'updateFields',entityId,entityData})
      if(data?.error){
        return oldRow;
      }
      return newRow;
    };

    const handleRowUpdateError=(error) => {
      consoleLog({file:file,context:'handleRowUpdateError',arg_name:'error',arg_value:error});
    };

    const baseSx={
      border:"none",
      "& .MuiDataGrid-columnHeaders":{
        backgroundColor:"#f5f5f5 !important",
      },
      "& .MuiDataGrid-columnHeader":{
        backgroundColor:"#f5f5f5 !important",
      },
    };

    const rawColumns=columns[list_name](
      isPopUp?(localColumnWidths??{}):(application.globalData.dataGridParams[list_name]??{}),
      application,
      handleClickEvent,
      entityRules,
      userData
    );
    const columnsForFilter=rawColumns
      .filter(col=>col.headerName)
      .map(col=>({field:col.field,headerName:col.headerName}));
    const computedColumns=quickSearchColumns
      ?rawColumns.map(col=>
          quickSearchColumns.includes(col.field)
            ?col
            :{...col,getApplyQuickFilterFn:()=>null}
        )
      :rawColumns;
   
    return (
      <div
        className="page-list-content"
        ref={contentRef}
        onClick={(e)=>{
          if(e.target.closest('.MuiInputBase-root')) return;
          if (!shouldFocusSearch) {
    setShouldFocusSearch(false);
  }}}>
        <Stack direction={{xs:'column',sm:'row'}} spacing={{xs:1,sm:2,md:2}}>
            <div style={{height:gridHeight,width:'100%'}}>
                <DataGrid
                    localeText={getLocale(application.globalData.lang)}
                    sx={[baseSx,dataGridSx]}
                    editMode="cell"
                    isCellEditable={(params)=>{
                      if(!entityRules?.can_update) return false;
                      if(params.colDef.editable!==true) return false;
                      if(params.row.can_update_fields!=null){
                        return params.row.can_update_fields.split(',').includes(params.field);
                      }
                      return true;
                    }}
                    processRowUpdate={processRowUpdate}
                    onProcessRowUpdateError={handleRowUpdateError}
                    experimentalFeatures={{newEditingApi:true}}
                    rows={records}
                    columns={computedColumns}
                    loading={loading}
                    isRowSelectable={(params)=>{
                      //Row selection defaults to meaning "selected for deletion" throughout this
                      //app, hence the can_delete/can_delete_row gate - a caller that uses
                      //checkboxSelection for something else entirely (not delete) opts out with
                      //selectionRequiresDeletePermission={false}, and canRemoveSelection (below)
                      //is gated the same way so the delete button can't appear either.
                      if(!selectionRequiresDeletePermission) return true;
                      if(entityRules?.can_delete===false) return false;
                      return Boolean(params?.row?.can_delete_row);//from the SQL literal "can_delete_row" . . . not from record logic -> faster for multiple records returned
                    }}
                    checkboxSelection={!!checkboxSelection}
                    disableRowSelectionOnClick={!!disableRowSelectionOnClick}
                    disableSelectionOnClick={!!disableSelectionOnClick}
                    getRowClassName={getRowClassName}
                    density="compact"
                    showToolbar
                    slots={{toolbar:CombinedToolbar}}
                    slotProps={{
                      toolbar:{
                        searchProps:{
                          filterModel: {
                            items: filterModel?.items ?? [],
                            quickFilterValues: filterModel?.quickFilterValues ?? [],
                          },
                        setFilterModel,
                        searchLabel:application.getLocale("SearchLabel"),
                        searchTooltip:application.getLocale("SearchTooltip"),
                        focusOnSearchInput: shouldFocusSearch,
                        application:application,
                        list_name:list_name,
                        isPopUp:isPopUp,
                        columnsForFilter,
                        quickSearchColumns,
                        setQuickSearchColumns,
                        },
                      buttonProps: {
                    application,
                  button_actions,
                  canRemoveSelection,
                  canCreateNew,
                  entityRules,
                  noRefresh,
                  filterComponent,
                  handleClickEvent,
                },
              },
            }}
                    initialState={initialState}
                    filterModel={filterModel}
                    onFilterModelChange={(newFilterModel)=>{
                        consoleLog({file:file,context:'onFilterModelChange',arg_name:'newFilterModel',arg_value:newFilterModel});
                        setFilterModel(newFilterModel);
                        if(!isPopUp){
                          application.updateGlobalData({...application.globalData,filterModel:{...application.globalData.filterModel,[list_name]:newFilterModel}});
                        }
                    }}
                    sortModel={sortModel}
                    onSortModelChange={(newSortModel)=>{
                        consoleLog({file:file,context:'onSortModelChange',arg_name:'newSortModel',arg_value:newSortModel});
                        setSortModel(newSortModel);
                        if(!isPopUp){
                            application.updateGlobalData({...application.globalData,sortModel:{...application.globalData.sortModel,[list_name]:newSortModel}});
                        }
                    }}
                    onColumnWidthChange={(params,event,details)=>{
                        const def={field:params.colDef.field,width:params.width}
                        consoleLog({file:file,context:'onColumnWidthChange',arg_name:'def',arg_value:def});
                        if(isPopUp){
                            const newLocalWidths={...localColumnWidths,[def.field]:{width:parseFloat(def.width.toFixed(2))}};
                            setLocalColumnWidths(newLocalWidths);
                            const listGridUpdatedNew={...application.globalData.dataGridParams,[list_name]:newLocalWidths};
                            application.updateGlobalData({...application.globalData,dataGridParams:listGridUpdatedNew});
                            saveUserDataGridParams(listGridUpdatedNew,userData.user_id);
                        }
                        else{
                            const listGridUpdatedNew=listGridUpdated(application.globalData.dataGridParams,list_name,def.field,def.width);
                            application.updateGlobalData({...application.globalData,dataGridParams:listGridUpdatedNew});
                            saveUserDataGridParams(listGridUpdatedNew,userData.user_id);
                        }
                    }}
                    onPaginationModelChange={(model)=>{
                        consoleLog({file:file,context:'onPaginationModelChange',arg_name:'model',arg_value:model});
                    }}
                    {...(rowSelectionModel!==undefined?{rowSelectionModel}:{})}
                    onRowSelectionModelChange={(selectionModel)=>{
                        handleSelection(selectionModel);
                    }}
                />
            </div>
        </Stack>
      </div>
    );
}

export default List;
